import { useLazyQuery, useMutation } from "@apollo/client";
import { Button, createStyles, makeStyles, Typography } from "@material-ui/core";
import manlaptop from "assets/images/man_with_laptop.svg";
import { DeleteDialog } from "components/DeleteDialog";
import { ContainerElements } from "components/PaginationList/Styles";
import SearchBox from "components/SearchBox";
import Table from "components/Table";
import { ROW_PER_PAGE_OPTIONS } from "config";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { ReceiverByClientID } from "domains/Task/graphql/queries";
// eslint-disable-next-line camelcase
import { Profile_User, Receiver } from "generated/graphql";
import { useCurrentUser } from "hooks";
import { useDebounce } from "hooks/useDebounce";
import { useDidMountEffect } from "hooks/useDidMountEffect";
import React, { useEffect, useState } from "react";
import { ADD_RECEIVER, DELETE_RECEIVER, UPDATE_RECEIVER } from "../graphql/mutations";
import { useReceiversColumns } from "../types/Columns";
import { useReceiverActions } from "./data";
import NewReceiverModal from "./NewReceiver";

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      display: "flex",
      flexDirection: "column",
      height: "100vh",
    },
    top: {
      flex: 1,
      backgroundColor: "#fff",
      borderRadius: 15,
      marginBottom: 20,
      display: "flex",
      justifyContent: "space-between",
    },
    title: {
      textAlign: "left",
    },
    bottom: {
      flex: 5,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      backgroundColor: "#fff",
      paddingLeft: 15,
      paddingRight: 15,
      borderRadius: 10
    },
    slogan: {
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-evenly",
      padding: 15,
    },
    img: {
      paddingRight: 0,
    },
    b1: {
      display: "block",
      paddingTop: 10,
      paddingBottom: 10,
    },
    tables: {
      height: "57vh",
      overflowX: "unset",
      overflowY: "scroll",
    },
    search: {
      width: 321,
    },
  })
);
export const ReceiverCreation = () => {
  const classes = useStyles();
  const { text } = useText();
  const { user } = useCurrentUser();
  const [searchTerm, setSearchTerm] = useState(null);
  const [isCurrentUser, setCurrentUser] = useState(false);
  const [dataToShow, setDataToShow] = useState([]);
  const [queryReceiver, { data, loading, refetch }] = useLazyQuery(ReceiverByClientID, { fetchPolicy: "network-only" });
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [addReceiver] = useMutation(ADD_RECEIVER);
  const [updateReceiver] = useMutation(UPDATE_RECEIVER);
  const [deleteReceiver] = useMutation(DELETE_RECEIVER);
  const receiverListColumns = useReceiversColumns();
  const dispatchAlert = useAlertDispatch();
  const [current, setCurrent] = useState<Receiver>(null);
  const [currentReceiverId, setCurrentReceiverId] = useState<Receiver>(null);
  const [page, setPage] = useState(0);
  const [countTotal, setCountTotal] = React.useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const subtaskActions = useReceiverActions(text);

  useEffect(() => {
    getReceiversByClient();
  }, [page, rowsPerPage]);

  useEffect(() => {
    if (user?.company_user_profil) setCurrentUser(user?.company_user_profil !== Profile_User.Admin);
  }, [user]);

  useEffect(() => {
    if (data) {
      if (data.allReceiversByClient?.code === 200) {
        const {
          allReceiversByClient: { data: receivers },
        } = data;
        setDataToShow(receivers);
        setCountTotal(data.allReceiversByClient.countTotal);
        setCurrentPage(data.allReceiversByClient.currentPage);
      } else {
        return dispatchAlert({
          message: text.all.error_getting_data,
          severity: "error",
        });
      }
    }
  }, [data]);

  useDidMountEffect(() => {
    setSearchTerm(debouncedSearchTerm);
  }, [debouncedSearchTerm]);

  useDidMountEffect(() => {
    page === 0 ? getReceiversByClient() : setPage(0);
  }, [rowsPerPage, debouncedSearchTerm, page]);

  const getReceiversByClient = () => {
    queryReceiver({
      variables: {
        params: { clientID: user?.company_user_clientId },
        queries: {
          page: page + 1,
          limit: rowsPerPage,
          searchText: debouncedSearchTerm,
        },
      },
    });
  };

  const onSearch = (event: any) => {
    event.preventDefault();
    event.persist();
    setSearchTerm(event.target.value);
  };

  const handleCloseModal = () => {
    setOpenDialog(false);
    dispatchAlert({ message: "The receiver is cancelled", severity: "warning" });
  };

  const handleCloseDeletionModal = () => {
    setOpenDeleteDialog(false);
    dispatchAlert({ message: "Receiver Deletion is cancelled", severity: "warning" });
  };
  /**
   * take as parameters receiver or receiver[] then set  then as order job
   * @param receivers
   */
  const saveReceiver = async (receivers: Receiver) => {
    if (current) {
      await updateReceiver({
        variables: {
          input: {
            receiverID: current._id,
            receiver: receivers,
          },
        },
      }).then(() => {
        setOpenDialog(false);
        setCurrent(null);
        refetch();
        dispatchAlert({ message: "The receiver is updated successfully!", severity: "success" });
      });
    } else {
      await addReceiver({
        variables: {
          input: {
            receiver: receivers,
            clientID: user?.company_user_clientId,
          },
        },
      }).then((res) => {
        const results = res?.data?.addReceiver;
        if (results?.code === 400) {
          dispatchAlert({ message: results?.message, severity: "error" });
        } else {
          dispatchAlert({ message: "The receiver is created successfully!", severity: "success" });
        }
        refetch();
        setOpenDialog(false);
      });
    }
  };

  /**
   * take as parameters receiver
   * @param receivers
   */
  const handleDeleteReceiver = async () => {
    await deleteReceiver({
      variables: {
        input: {
          id: currentReceiverId,
        },
      },
    }).then((res) => {
      refetch();
      setOpenDeleteDialog(false);
      const results = res?.data?.deleteReceiver;
      if (results?.code === 200) {
        dispatchAlert({ message: "The receiver is created successfully!", severity: "success" });
      } else {
        dispatchAlert({ message: results?.message, severity: "error" });
      }
    });
  };

  const deleteReceiverHandler = (data: any) => {
    setCurrentReceiverId(data?._id);
    setOpenDeleteDialog(true);
  };
  const editReceiverHandler = (data: any) => {
    setCurrent(data);
    setOpenDialog(true);
  };
  const handleAction = (key: string, row: any) => {
    switch (key) {
      case "edit":
        return editReceiverHandler(row);
      case "delete":
        return deleteReceiverHandler(row);
      // no default
    }
  };

  return (
    <div className={classes.root}>
      <div className={classes.top}>
        <div className={classes.slogan}>
          <div
            css={`
              font-size: 36px;
              font-weight: 900;
              font-stretch: normal;
              font-style: normal;
              line-height: 1.39;
              color: rgba(117, 122, 147, 0.64);
            `}
          >
            <Typography variant="h3">
              {text.client.manage_your_client} <b>{text.all.easily}</b>
            </Typography>
          </div>

          <div>
            <Button color="primary" variant="contained" onClick={() => setOpenDialog(true)} disabled={isCurrentUser}>
              {text.all.add_client}
            </Button>
          </div>
        </div>
        <div className={classes.img}>
          <img
            src={manlaptop}
            width="150px"
            css={`
              margin-top: -10px;
            `}
            alt="man with laptop"
          />
        </div>
      </div>

      <div className={classes.bottom}>
        <div className={classes.b1}>
          <Typography
            variant="h1"
            css={`
              color: #1a224b;
            `}
          >
            Clients
          </Typography>
          <ContainerElements>
            <SearchBox className={classes.search} onChange={onSearch} />
          </ContainerElements>
        </div>
        <div className={classes.tables}>
          <Table
            rowsIdentifier="_id"
            data={dataToShow}
            columns={receiverListColumns}
            isLoading={loading}
            hasSelection
            isShowMenu
            onRowMenuClick={handleAction}
            multipleActions={subtaskActions}
            onRowClick={() => {}}
            advancedControl
            rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
            setRowsPerPage={setRowsPerPage}
            rowsPerPage={rowsPerPage}
            currentPage={page}
            countTotal={countTotal}
          />
        </div>
      </div>

      <NewReceiverModal
        open={openDialog}
        handleClose={handleCloseModal}
        handleNew={saveReceiver}
        key="receiver modal"
        current={current}
      />
      <DeleteDialog
        title={text.receiver.receiver_deletion}
        message={text.receiver.message_deletion}
        open={openDeleteDialog}
        handlerCancel={handleCloseDeletionModal}
        handlerConfirm={handleDeleteReceiver}
      />
    </div>
  );
};

export default ReceiverCreation;
